/* The Damga mark, as geometry rather than as a copied path string.
 *
 * NAMED mark-geometry AND NOT mark, because Mark.tsx sits beside it and two
 * files differing only in case resolve to each other on a case-insensitive
 * filesystem and to different files on a case-sensitive one. It compiled on
 * macOS as a circular self-import and would have behaved differently in CI.
 *
 * ONE SOURCE, BECAUSE THERE WERE THREE
 * The path was pasted into the React component, the icon generator and the
 * front-door page. Three copies of a logo is how a logo quietly stops being
 * one shape, and this file is plain JavaScript precisely so the node scripts
 * and the TypeScript components can both read it.
 *
 * THE SPEC, FROM THE BRAND SHEET, IN UNITS OF BLOCK WIDTH (1000)
 *   corner radii   264 / 264 / 264 / 50, the squared corner ALWAYS bottom-left
 *   counter        height 514, inset left 193, top 243
 *   stem           86
 *   bowl           329, full round
 *
 * WHY THE COUNTER IS 415 WIDE AND NOT 329
 * The counter is the stem plus the bowl: 86 + 329, from x=193 to x=608. The
 * bowl being "full round" makes its right edge a semicircle of radius 514/2,
 * so the straight run along the top ends at 608 - 257 = 351.
 *
 * The prototypes draw something different: they knock out the bowl alone,
 * 329 wide starting at x=264, with no stem at all. The handoff says in as many
 * words that the prototypes are wrong about this mark and that it "needs a
 * vector redraw before print or filing", so the written spec wins. Measured
 * from the brand sheet's own large specimen, everything else agrees to within
 * a third of a unit.
 *
 * AND THE COUNTER IS A REAL KNOCKOUT
 * One path, `fill-rule: evenodd`, so whatever is behind the mark shows through
 * the D. The prototypes fill it with the background colour, which is identical
 * until the mark sits on anything else and obviously wrong the moment it does.
 * On a home screen it would sit on the wallpaper.
 */

/** Every number in the brand sheet, in units of block width. */
export const MARK_UNITS = {
  block: 1000,
  radius: { topLeft: 264, topRight: 264, bottomRight: 264, bottomLeft: 50 },
  counter: { insetLeft: 193, insetTop: 243, height: 514, stem: 86, bowl: 329 },
};

const U = MARK_UNITS;

/** Derived, so the arithmetic is stated once and can be asserted. */
export const MARK_GEOMETRY = (() => {
  const left = U.counter.insetLeft;
  const top = U.counter.insetTop;
  const height = U.counter.height;
  const width = U.counter.stem + U.counter.bowl;
  const radius = height / 2;
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    radius,
    /** Where the straight run along the counter's top gives way to the bowl. */
    shoulder: left + width - radius,
    /** Both should land on the block's own centre line, per the brand sheet's
     *  "vertically centred, horizontally left of centre". */
    centreY: top + height / 2,
    centreX: left + width / 2,
  };
})();

/** The mark as one path. Fill it with `fill-rule="evenodd"` and nothing else. */
export function markPath() {
  const { topLeft: tl, topRight: tr, bottomRight: br, bottomLeft: bl } = U.radius;
  const b = U.block;
  const g = MARK_GEOMETRY;

  const block =
    `M${tl},0 ` +
    `H${b - tr} A${tr},${tr} 0 0 1 ${b},${tr} ` +
    `V${b - br} A${br},${br} 0 0 1 ${b - br},${b} ` +
    `H${bl} A${bl},${bl} 0 0 1 0,${b - bl} ` +
    `V${tl} A${tl},${tl} 0 0 1 ${tl},0 Z`;

  const counter =
    `M${g.left},${g.top} ` +
    `H${g.shoulder} A${g.radius},${g.radius} 0 0 1 ${g.shoulder},${g.bottom} ` +
    `H${g.left} Z`;

  return `${block} ${counter}`;
}

/** A standalone file, for anywhere that cannot take a component: the icon
 *  generator, the front door, a hand-off to a printer. */
export function markSvg(color = "currentColor") {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${U.block} ${U.block}" ` +
    `role="img" aria-label="Damga">\n` +
    `  <title>Damga</title>\n` +
    `  <path fill="${color}" fill-rule="evenodd" d="${markPath()}"/>\n` +
    `</svg>\n`
  );
}
